import { GridItem, Image, SimpleGrid, Stack, WrapItem, useBreakpointValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';
import colors from '../../../constants/colors';
import { getPhotoCardConfirmedStatus } from '../../../utils/PhotoCardUtil';
import { EventNameBox } from '../../event/EventNameBox';
import CheckIcon from '../../icons/CheckIcon';

const EventNameBoxWrapper = styled.div`
  overflow: hidden;
  padding-top: 20px;
  padding-bottom: 9px;
`;

const IconWrapper = styled.div`
  background-color: ${colors.primary};
  border-top-left-radius: 8px;
  border-bottom-right-radius: 8px;
  padding: 3px;
`;

const StatusBadge = styled.div`
  width: fit-content;
  display: inline-block;
  border-radius: 4px;
  padding: 4px 8px;
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
`;

export default function PhotoCardsByEventEditor({
  events,
  onClickedPhotoCard,
  endReached,
  isColorMode,
  isAdminMode = false
}) {
  const isEnabledCard = (photoCard) => {
    if (isAdminMode) {
      return photoCard.isChecked ?? false;
    }
    return photoCard.collectedCount > 0;
  };

  const columns = useBreakpointValue({
    base: 4, // 모바일
    md: 6, // iPad
    lg: 6 // 큰 화면
  });

  // #region draw cards
  const drawCardsWithEventName = (e) => {
    if (isEmpty(e.photoCards)) {
      return <div className="h-px" />;
    }
    return (
      <Stack key={e.eventId} paddingBottom={'4px'}>
        <EventNameBoxWrapper>
          <EventNameBox event={e} />
        </EventNameBoxWrapper>
        <SimpleGrid
          columns={columns}
          spacing={'4px'}
          marginInlineStart={0}
          marginTop={'0px!important'}
        >
          {e.photoCards?.map((photoCard) => {
            const pendingStatus = getPhotoCardConfirmedStatus(photoCard);
            return (
              <motion.div
                key={photoCard.photoCardId}
                initial={{ opacity: 0, y: -1 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -1 }}
                transition={{ duration: 0.5 }}
              >
                <GridItem aspectRatio={'55/87'}>
                  <WrapItem className="relative" aspectRatio={'55/87'}>
                    {isAdminMode && (
                      <StatusBadge
                        className={`absolute top-0 left-0 z-50 text-white ${
                          pendingStatus === 'approved'
                            ? 'bg-primary-light dark:bg-primary-dark '
                            : pendingStatus === 'pending'
                              ? 'bg-main-light-2 dark:bg-main-dark-2'
                              : 'bg-warning'
                        }`}
                      >
                        {t(`ReportedPhotoCardsPage.${pendingStatus}`)}
                      </StatusBadge>
                    )}

                    <Image
                      src={photoCard.mediumThumbnailUrl || photoCard.pictureUrl}
                      className={
                        isColorMode
                          ? 'grayscale-0'
                          : isEnabledCard(photoCard)
                            ? 'grayscale-0'
                            : 'grayscale'
                      }
                      aspectRatio={'55/87'}
                      width={'100%'}
                      objectFit={'cover'}
                      border={isEnabledCard(photoCard) ? '2px' : ''}
                      borderColor={isEnabledCard(photoCard) ? 'secondary.1000' : ''}
                      borderRadius="8px"
                      onClick={() => onClickedPhotoCard(photoCard, e.event)}
                      style={{ boxSizing: 'border-box' }}
                      loading="lazy"
                    />
                    {isEnabledCard(photoCard) && (
                      <IconWrapper className="absolute right-0 bottom-0">
                        <CheckIcon />
                      </IconWrapper>
                    )}
                  </WrapItem>
                </GridItem>
              </motion.div>
            );
          })}
        </SimpleGrid>
      </Stack>
    );
  };
  // #endregion

  return (
    <Virtuoso
      useWindowScroll
      style={{ height: '100%' }}
      endReached={endReached}
      overscan={480}
      data={events}
      itemContent={(_, e) => {
        return (
          <motion.div
            key={e.eventId}
            initial={{ opacity: 0, y: -1 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -1 }}
            transition={{ duration: 0.5 }}
          >
            {drawCardsWithEventName(e)}
          </motion.div>
        );
      }}
    />
  );
}
